class AjaxSearch {
  constructor() {
    this.selectors = {
      searchForm: '#header-search form',
      searchInput: '#header-search input[type="search"]',
      searchContainer: '#header-search',
      searchResultsContainer: '[data-nav-search-results]',
      searchType: '#header-search [name="options[prefix]"]',
      pagination: '.pagination',
      resultsList: '.results__list',
      loadingIndicator: '.pagination__loading',
      popularSearchLinks: '[data-popular-search-link]'
    };

    this.classes = {
      isSearching: 'is-searching',
      pushUp: 'push-up'
    };

    this.searchRequest = null;
    this.abortController = null;
    this.isLoading = false;
    this.currentQuery = '';
    this.resultsPerPage = 250;
    this.debounceTimer = null;

    this.init();
  }

  init() {
    this.setupEventListeners();
  }

  setupEventListeners() {
    // See More button functionality
    const seeMoreButton = document.querySelector('.see-more-button');
    if (seeMoreButton) {
      seeMoreButton.addEventListener('click', this.handleSeeMoreClick.bind(this));
    }

    // Search input functionality
    const searchInput = document.querySelector(this.selectors.searchInput);
    if (searchInput) {
      searchInput.addEventListener('input', (event) => {
        const searchTerm = event.target.value.trim();
        
        if (this.debounceTimer) {
          clearTimeout(this.debounceTimer);
        }
        
        if (searchTerm) {
          this.debounceTimer = setTimeout(() => {
            this.currentQuery = searchTerm;
            this.request(searchTerm);
          }, 300);
        } else {
          this.clearSearchResults();
        }
      });
    }

    // AJAX Search for popular search links
    const popularSearchLinks = document.querySelectorAll(this.selectors.popularSearchLinks);

    if (popularSearchLinks.length > 0 && searchInput) {
      popularSearchLinks.forEach(link => {
        link.addEventListener('click', this.handlePopularSearchClick.bind(this));
      });
    }

    // Handle search open/close events
    const searchContainer = document.querySelector(this.selectors.searchContainer);
    if (searchContainer) {
      // Watch for Alpine.js changes to active class
      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.attributeName === 'class') {
            const isActive = searchContainer.classList.contains('active');
            if (isActive) {
              document.documentElement.classList.add('search-active');
              document.body.classList.add('search-active');
            } else {
              document.documentElement.classList.remove('search-active');
              document.body.classList.remove('search-active');
            }
          }
        });
      });

      observer.observe(searchContainer, { attributes: true });
    }

    // Handle close button
    const closeButton = document.querySelector('#close-header-search');
    if (closeButton) {
      closeButton.addEventListener('click', () => {
        this.closeSearch();
      });
    }
  }

  handleSeeMoreClick(event) {
    const resultsPopular = document.querySelector('.results-popular.hide-catalog');
    if (!resultsPopular) return;

    const isVisible = resultsPopular.style.display !== 'none';

    if (isVisible) {
      resultsPopular.style.display = 'none';
      event.target.textContent = 'See More';
    } else {
      resultsPopular.style.display = 'block';
      event.target.textContent = 'See Less';
    }
  }

  handlePopularSearchClick(event) {
    event.preventDefault();

    // Get the correct element (handle both the link itself and potential child elements)
    const linkElement = event.target.closest('a[data-popular-search-link]');
    if (!linkElement) return;

    // Get the search term from the link text or href parameter
    let searchTerm = linkElement.textContent.trim();

    // Check if URL has a 'q' parameter - if so, use that instead
    const url = new URL(linkElement.href, window.location.origin);
    const queryParam = url.searchParams.get('q');
    if (queryParam) {
      searchTerm = queryParam;
    }

    // Update the search input with the search term
    const searchInput = document.querySelector(this.selectors.searchInput);
    if (searchInput) {
      searchInput.value = searchTerm;
    }

    // Store the current query
    this.currentQuery = searchTerm;

    // Perform AJAX search
    this.request(searchTerm);

    // Make search overlay visible if it's not already
    const headerSearch = document.querySelector(this.selectors.searchContainer);
    if (headerSearch && !headerSearch.classList.contains('active')) {
      headerSearch.classList.add('active');
      // Add search-active class to html and body
      document.documentElement.classList.add('search-active');
      document.body.classList.add('search-active');
    }
  }

  // Add function to close search and restore scrolling
  closeSearch() {
    const headerSearch = document.querySelector(this.selectors.searchContainer);
    if (headerSearch && headerSearch.classList.contains('active')) {
      headerSearch.classList.remove('active');
      // Remove search-active class from html and body
      document.documentElement.classList.remove('search-active');
      document.body.classList.remove('search-active');
    }
  }

  clearSearchResults() {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = null;
    }
    
    if (this.abortController) {
      this.abortController.abort();
      this.abortController = null;
    }
    
    const searchResultsContainer = document.querySelector(this.selectors.searchResultsContainer);
    if (searchResultsContainer) {
      searchResultsContainer.innerHTML = '';
    }
    
    const searchContainer = document.querySelector(this.selectors.searchContainer);
    if (searchContainer) {
      searchContainer.classList.remove(this.classes.isSearching, this.classes.pushUp);
    }

    this.currentQuery = '';
    this.isLoading = false;
  }

  request(searchQuery) {
    const searchType = document.querySelector(this.selectors.searchType)?.value || 'last';
    const searchResultsContainer = document.querySelector(this.selectors.searchResultsContainer);
    const searchContainer = document.querySelector(this.selectors.searchContainer);

    if (!searchResultsContainer || !searchContainer) return;

    // Clear previous results
    searchResultsContainer.innerHTML = '';

    // Add the loading indicator with SVG and animated dots
    const loadingHTML = `
      <div class="pagination__loading">
        <div class="loading-text">
          Loading more
          <span class="dots dots--animated">
            <span>.</span>
            <span>.</span>
            <span>.</span>
          </span>
        </div>
      </div>
    `;
    searchResultsContainer.innerHTML = loadingHTML;

    // Show searching state
    searchContainer.classList.add(this.classes.isSearching);

    // Set loading state
    this.isLoading = true;

    // Kill previous ajax request
    if (this.searchRequest && this.searchRequest.signal && !this.searchRequest.signal.aborted) {
      this.abortController?.abort();
    }

    this.abortController = new AbortController();

    // First attempt: using view-json with limit parameter
    const searchUrl = `/search?view=view-json&q=${encodeURIComponent(searchQuery)}&type=product&limit=${this.resultsPerPage}&page=1`;

    fetch(searchUrl, {
      signal: this.abortController.signal
    })
      .then(response => {
        if (!response.ok) {
          throw new Error(`Search API returned status ${response.status}`);
        }
        return response.text();
      })
      .then(text => {
        try {
          // Try to find JSON in the response (in case there's surrounding HTML)
          let jsonText = text;
          const jsonStart = text.indexOf('{');
          const jsonEnd = text.lastIndexOf('}') + 1;

          if (jsonStart >= 0 && jsonEnd > jsonStart) {
            jsonText = text.substring(jsonStart, jsonEnd);
          }

          // Check if the response is valid JSON
          const data = JSON.parse(jsonText);
          return data;
        } catch (e) {
          // Try alternative approach with .json format
          const alternativeUrl = `/search.json?q=${encodeURIComponent(searchQuery)}&type=product&limit=${this.resultsPerPage}`;

          return fetch(alternativeUrl)
            .then(response => response.json())
            .then(jsonData => {
              return this.convertShopifyApiFormat(jsonData, searchQuery);
            })
            .catch(jsonError => {
              // Try fallback to standard json liquid template
              return fetch(`/search?view=json&q=${encodeURIComponent(searchQuery)}&type=product&limit=${this.resultsPerPage}`)
                .then(response => response.text())
                .then(fallbackText => {
                  try {
                    // Try to find JSON in the response
                    let jsonText = fallbackText;
                    const jsonStart = fallbackText.indexOf('{');
                    const jsonEnd = fallbackText.lastIndexOf('}') + 1;

                    if (jsonStart >= 0 && jsonEnd > jsonStart) {
                      jsonText = fallbackText.substring(jsonStart, jsonEnd);
                    }

                    return JSON.parse(jsonText);
                  } catch (fallbackError) {
                    throw new Error('Invalid JSON response from search endpoints');
                  }
                });
            });
        }
      })
      .then(data => {
        let searchResultsContent = '';

        // Filter to only show products
        const productResults = data.results.filter(item => item.object_type === 'product');
        const productCount = productResults.length;

        if (productCount > 0) {
          // Match the exact format from the original code
          const resultsText = productCount == 1
            ? productCount + ' result'
            : productCount + ' results';

          searchResultsContent += '<div class="results__count">' + resultsText + '</div>';
          searchResultsContent += '<ul class="results__list product-grid--' + (window.settings?.productGridLayout || '') + '">' + this.getResultsContent({ ...data, results: productResults }) + '</ul>';
        } else {
          searchResultsContent = '<p class="no-results">No results found for "' + searchQuery + '". Check the spelling or use a different word or phrase for '+ searchQuery +'</p>';
        }

        searchResultsContainer.innerHTML = searchResultsContent;
        searchContainer.classList.remove(this.classes.isSearching);

        // Set loading state
        this.isLoading = false;
      })
      .catch(error => {
        if (error.name === 'AbortError') {
          return;
        }

        // Show error message
        searchResultsContainer.innerHTML = '<div class="results-empty">Error retrieving search results: ' + error.message + '. Please try again.</div>';
        searchContainer.classList.remove(this.classes.isSearching);

        // Set loading state
        this.isLoading = false;
      });
  }

  // Helper method to convert from Shopify Search API format to our expected format
  convertShopifyApiFormat(shopifyData, searchQuery) {
    if (!shopifyData || !shopifyData.resources || !shopifyData.resources.results) {
      return { results: [] };
    }

    const results = shopifyData.resources.results.products.map(product => {
      return {
        title: product.title,
        url: `/products/${product.handle}`,
        featured_image: product.image ? product.image.src : null,
        image_aspectratio: product.image ? '1/1' : '1/1', // Default aspect ratio
        available: product.available,
        price: product.price_min,
        compare_at_price: product.compare_at_price_min || product.price_min,
        object_type: 'product'
      };
    });

    return {
      results: results,
      query: searchQuery,
      next_page: null
    };
  }

  getResultsContent(data) {
    let searchResultsContent = '';

    for (let i = 0; i < data.results.length; i++) {
      const result = data.results[i];
      const title = result.title;
      const url = result.url;
      // Get full size image by replacing the image URL parameters
      const image = result.featured_image ? result.featured_image.replace('_360x', '_1200x') : null;
      const imageAspectRatio = result.image_aspectratio;
      let badge = '';

      // Check if Sale or Sold out
      if (result.available === false) {
        badge = '<div class="tag sold-out"><span>SOLD OUT</span></div>';
      } else if (result.price < result.compare_at_price) {
        badge = '<div class="tag on-sale"><span>SALE</span></div>';
      }

      // Use an image tag instead of background-image, remove price display, center title
      searchResultsContent += `
        <li class="result">
          <div class="result__image">
            <a href="${url}" class="result__image-link">
              ${image ?
          `<img src="${image}" alt="${title}" class="result__image-img product__media lazyload fade-in" 
                  loading="lazy" />`
          :
          `<div class="result__image-img product__media"></div>`
        }
              ${badge}
            </a>
          </div>
          <a href="${url}" class="result__title-link">
            <p class="result__title">${title}</p>
          </a>
        </li>
      `;
    }

    return searchResultsContent;
  }

  // Utility functions
  bgset(image, aspectRatio) {
    if (!image) return '';

    return `${image} 180w, ${image} 360w, ${image} 540w, ${image} 720w, ${image} 900w, ${image} 1080w, ${image} 1296w, ${image} 1512w, ${image} 1728w, ${image} 1944w, ${image} 2160w, ${image} 2376w, ${image} 2592w, ${image} 2808w, ${image} 3024w`;
  }

  formatMoney(cents) {
    if (typeof cents === 'string') {
      cents = cents.replace('.', '');
    }

    const value = (cents / 100).toFixed(2);
    const moneyFormat = window.theme && window.theme.moneyFormat
      ? window.theme.moneyFormat
      : '${{amount}}';

    return moneyFormat.replace(/\{\{\s*(\w+)\s*\}\}/, value);
  }
}

// Initialize on DOM content loaded
document.addEventListener('DOMContentLoaded', function () {
  new AjaxSearch();
}); 